import type { MemoryConfig } from '@/types/typesMemory';
import { ConfigurationError } from '@/utils/azureOpenAi/errorHandler';

class MemoryConfigManager {
  private static instance: MemoryConfigManager;
  private config: MemoryConfig;

  private constructor() {
    this.config = this.validateAndCreateConfig();
  }

  public static getInstance(): MemoryConfigManager {
    if (!MemoryConfigManager.instance) {
      MemoryConfigManager.instance = new MemoryConfigManager();
    }
    return MemoryConfigManager.instance;
  }
  
  // validación de configuración 
  private validateAndCreateConfig(): MemoryConfig {
    const requiredEnvVars = [
      'VITE__QDRANT_URL',
      'VITE__QDRANT_TOKEN',
      'VITE__QDRANT_COLLECTION',
      'VITE__AZURE_EMBEDDING_ENDPOINT',
      'VITE__AZURE_EMBEDDING_DEPLOYMENT_ID',
      'VITE__AZURE_EMBEDDING_API_VERSION',
      'VITE__AZURE_OPENAI_API_KEY',
      'VITE__AZURE_OPENAI_ENDPOINT',
      'VITE__AZURE_DEPLOYMENT_ID',
      'VITE__AZURE_API_VERSION',
      'VITE__AZURE_SPEECH_REGION',
      'VITE__BACKEND_API_URL'
    ];

    // Lanza error si falta alguna variable de entorno
    const missing = requiredEnvVars.filter(envVar => !import.meta.env[envVar]);
    if (missing.length > 0) {
      throw new ConfigurationError(`Missing required environment variables: ${missing.join(', ')}`);
    }

    return {
      // configuración de qdrant
      qdrant: {
        url: import.meta.env.VITE__QDRANT_URL,
        token: import.meta.env.VITE__QDRANT_TOKEN,
        collection: import.meta.env.VITE__QDRANT_COLLECTION
      },
      // configuración de azure embedding
      azureEmbedding: {
        endpoint: import.meta.env.VITE__AZURE_EMBEDDING_ENDPOINT,
        apiKey: import.meta.env.VITE__AZURE_OPENAI_API_KEY,
        apiVersion: import.meta.env.VITE__AZURE_EMBEDDING_API_VERSION,
        deploymentId: import.meta.env.VITE__AZURE_EMBEDDING_DEPLOYMENT_ID
      },
      // configuración de azure chat
      azureChat: {
        endpoint: import.meta.env.VITE__AZURE_OPENAI_ENDPOINT,
        apiKey: import.meta.env.VITE__AZURE_OPENAI_API_KEY,
        apiVersion: import.meta.env.VITE__AZURE_API_VERSION,
        deploymentId: import.meta.env.VITE__AZURE_DEPLOYMENT_ID
      },
      // configuración de Azure Speech
      azureSpeech: {
        apiKey: import.meta.env.VITE__AZURE_OPENAI_API_KEY,
        region: import.meta.env.VITE__AZURE_SPEECH_REGION,
        timeout: parseInt(import.meta.env.VITE__SPEECH_TIMEOUT || '10000'),
        useFallback: import.meta.env.VITE__USE_FALLBACK_TTS === 'true'
      },
      // configuración de API backend
      api: {
        backendUrl: import.meta.env.VITE__BACKEND_API_URL,
        timeout: parseInt(import.meta.env.VITE__API_TIMEOUT || '30000'),
        retries: parseInt(import.meta.env.VITE__API_RETRIES || '3')
      },
      // configuración de cache
      cache: {
        maxSize: parseInt(import.meta.env.VITE__CACHE_MAX_SIZE || '5000'),
        ttl: parseInt(import.meta.env.VITE__CACHE_TTL || '86400000') // 24h default
      }
    };
  }

  public getConfig(): MemoryConfig {
    return this.config;
  }

  public getQdrantConfig() {
    return this.config.qdrant;
  }

  public getAzureEmbeddingConfig() {
    return this.config.azureEmbedding;
  }

  public getAzureChatConfig() {
    return this.config.azureChat;
  }

  public getCacheConfig() {
    return this.config.cache;
  }

  public getAzureSpeechConfig() {
    return this.config.azureSpeech;
  }

  public getAPIConfig() {
    return this.config.api;
  }

  // Valida la configuración de Azure (core)
  public isConfigValid(): boolean {
    try {
      return Boolean(this.config.azureChat.apiKey && 
               this.config.azureChat.endpoint && 
               this.config.qdrant.url && 
               this.config.qdrant.token);
    } catch {
      return false;
    }
  }

  // Valida la configuración de Azure Speech
  public isSpeechConfigValid(): boolean {
    try {
      return Boolean(this.config.azureSpeech.apiKey && this.config.azureSpeech.region);
    } catch {
      return false;
    }
  }

  // Devuelve la configuración de reintento
  public getRetryConfig() {
    return {
      maxRetries: this.config.api.retries || 3,
      timeout: this.config.api.timeout || 30000
    };
  }
}

export default MemoryConfigManager;